import { DependencyList, useCallback, useEffect, useRef, useState } from 'react';

import { ApiMeta, Enveloped } from '../../../../../services/eventingApi';

export interface ApiResource<T> {
  data: T | null;
  meta: ApiMeta | null;
  loading: boolean;
  error: string | null;
  reload: () => void;
}

export function useApiResource<T>(loader: () => Promise<Enveloped<T>>, deps: DependencyList, enabled = true): ApiResource<T> {
  const [data, setData] = useState<T | null>(null);
  const [meta, setMeta] = useState<ApiMeta | null>(null);
  const [loading, setLoading] = useState(enabled);
  const [error, setError] = useState<string | null>(null);
  const [version, setVersion] = useState(0);
  const loaderRef = useRef(loader);
  loaderRef.current = loader;

  useEffect(() => {
    if (!enabled) return undefined;
    let active = true;
    setLoading(true);
    setError(null);
    loaderRef
      .current()
      .then((response) => {
        if (!active) return;
        setData(response.data);
        setMeta(response.meta);
      })
      .catch((reason: Error) => {
        if (active) setError(reason.message);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, version, enabled]);

  const reload = useCallback(() => setVersion((current) => current + 1), []);
  return { data, meta, loading, error, reload };
}
